import SDK from '../../../SDK';
import recentChatIdb from '../../../../Helpers/RecentChatIdb';
import { Store } from '../../../../store';
import { RecentChatAction } from '../../../../Actions/RecentChatActions';
import { BatchSetUnreadCounts } from '../../../../Actions/UnreadCount';
import { isMessageExpiredLocally } from '../../../../Helpers/Chat/DisappearScheduler';
import { CHAT_TYPE_GROUP } from '../../../../Helpers/Chat/Constant';
import { getActiveConversationChatJid } from '../../../../Helpers/Chat/ChatHelper';
import { getUserIdFromJid } from '../../../../Helpers/Utility';
import { SyncProgressAction } from '../../../../Actions/SyncProgressAction';
import logger from '../../../../Helpers/logger';
import { fetchAndUpdateGroupParticipants } from './GroupSync';
import { syncAllMessages, updatePreviousMessages } from './messagesSync';
import { batchUpdateLastSyncMessagesTime, CONFIG, updateLastSyncTime } from './config';
// ============================================================================
// CHAT SYNCHRONIZATION MODULE (Blocking Architecture)
// ============================================================================
// Fetches ALL recent chats from server → stores in IndexedDB → updates Redux → releases UI
// ============================================================================

// ============================================================================
// LOGGING
// ============================================================================

let chatSyncLogs = {};

const log = (id = 'Summary', message, ...args) => {
    if (!chatSyncLogs[id]) chatSyncLogs[id] = [];
    chatSyncLogs[id].push({ message, args, timestamp: new Date().toISOString() });
};

const logTime = (id, label, startTime) => {
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    if (!chatSyncLogs[id]) chatSyncLogs[id] = [];
    chatSyncLogs[id].push({ type: 'time', label, duration: `${duration}s`, timestamp: new Date().toISOString() });
    return duration;
};

function updateUnreadCountsFromChats(chats) {
    if (!Array.isArray(chats) || chats.length === 0) return;

    const unreadCountsMap = {};
    const len = chats.length;
    for (let i = 0; i < len; i++) {
        const chat = chats[i];
        const { fromUserId, unreadCount, unreadMessageId, msgId } = chat;
        let effectiveCount = fromUserId && unreadCount && unreadCount > 0 ? parseInt(unreadCount) : 0;

        // If the first unread message was locally expired by the disappear timer,
        // those unread messages were consumed before being read — treat count as 0.
        // This prevents the API (which doesn't know about local expiry) from restoring
        // a stale badge on page refresh.
        if (effectiveCount > 0 && unreadMessageId && isMessageExpiredLocally(unreadMessageId)) {
            effectiveCount = 0;
        }

        if (fromUserId) {
            unreadCountsMap[fromUserId] = {
                count: effectiveCount,
                unreadMsgId: effectiveCount > 0 ? unreadMessageId || '' : msgId || '',
            };
        }
    }

    const updatedCount = Object.keys(unreadCountsMap).length;
    if (updatedCount > 0) {
        Store.dispatch(BatchSetUnreadCounts(unreadCountsMap));
    }
}

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Core function to fetch chats with pagination
 * @param {Object} params - Fetch parameters
 * @param {string} params.direction - 'forward' or 'backward'
 * @param {string} [params.messageTime] - Timestamp for pagination
 * @param {number} [params.limit] - Fetch limit per batch
 * @returns {Promise<Array>} Accumulated chats
 */
async function fetchChatsWithPagination(
    { direction = 'backward', messageTime = null, limit = CONFIG.RECENT_CHAT_FETCH_LIMIT },
    onProgress,
) {
    const startTime = performance.now();
    const allChats = [];
    let currentMessageTime = messageTime;
    let batchCount = 0;
    const step = direction === 'forward' ? 'BackgroundSync' : 'Step 2';

    while (true) {
        batchCount++;
        const params = { limit, direction };
        if (currentMessageTime) params.messageTime = currentMessageTime;

        log(step, `Fetching batch ${batchCount} (${direction})...`);
        const response = await SDK.getRecentChatsByPagination(params);
        // response.data.splice(1); // Testing pagination.

        if (response?.statusCode !== 200 || !response.data || response.data.length === 0) {
            log(step, `Pagination complete after ${batchCount} batches`);
            break;
        }
        // response.data.splice(1); // TEMPORARY: Only take 1 chat per batch for testing pagination UI, remove this line later to fetch full batches
        allChats.push(...response.data);
        log(step, `Batch ${batchCount}: Fetched ${response.data.length} chats (Total: ${allChats.length})`);

        if (onProgress) {
            const estimatedProgress = Math.min(batchCount * 0.1, 0.9);
            onProgress(estimatedProgress);
        }

        // Check if we've reached the end
        if (response.data.length < limit) {
            log(step, `Last batch received (${response.data.length} < ${limit})`);
            break;
        }

        // Update cursor for next batch
        currentMessageTime = response.data[response.data.length - 1].messageTime;
    }

    logTime(step, `Fetched ${allChats.length} total chats`, startTime);
    return allChats;
}

/**
 * Fetches ALL recent chats from server with complete pagination
 * @returns {Promise<Array>} All recent chats
 */
async function fetchAllRecentChats(direction = 'backward', onProgress) {
    log('Step 2', '📥 Starting to fetch all recent chats from server');
    return fetchChatsWithPagination({ direction }, onProgress);
}

/**
 * Stores all chats in IndexedDB
 * @param {Array} chats - All recent chats
 */
async function storeChatsInDB(chats) {
    log('Step 3', `💾 Storing ${chats.length} chats in IndexedDB`);
    const startTime = performance.now();

    await recentChatIdb.setRecentChats(chats);

    logTime('Step 3', `Stored ${chats.length} chats in IndexedDB`, startTime);
}

/**
 * Updates Redux store with all chats
 * @param {Array} chats - All recent chats
 */
function updateReduxStore(chats) {
    log('Summary', `🔄 Updating Redux store with ${chats.length} chats`);
    const startTime = performance.now();

    Store.dispatch(RecentChatAction(chats));

    logTime('Summary', `Updated Redux store`, startTime);
}

/**
 * Fetches latest chats in background (forward direction)
 * @param {string} lastMessageTime - Timestamp of the latest cached chat
 */
async function fetchLatestChatsInBackground(lastMessageTime) {
    const logId = 'BackgroundSync';
    log(logId, `🕵️‍♂️ Checking for new chats since ${lastMessageTime || 'beginning'}`);

    if (!lastMessageTime) return;

    try {
        const api_fetch_time = lastMessageTime * 1000;
        const activeChatJid = getActiveConversationChatJid();
        const activeChatId = activeChatJid ? getUserIdFromJid(activeChatJid) : null;
        if (activeChatJid) {
            updatePreviousMessages({ toJid: activeChatJid, lastSyncTime: lastMessageTime });
        }
        const newChats = await fetchChatsWithPagination({
            direction: 'forward',
            messageTime: api_fetch_time,
        });

        if (newChats.length > 0) {
            log(logId, `🕵️‍♂️ Found ${newChats.length} new/updated chats`);
            Store.dispatch(RecentChatAction(newChats, true));
            updateUnreadCountsFromChats(newChats);

            syncAllMessages(newChats, { forceFetch: true });
            const groupChats = newChats.filter(chat => chat.chatType === CHAT_TYPE_GROUP);

            if (groupChats.length > 0) {
                groupChats.sort((a, b) => {
                    const aId = a.fromUserId;
                    const bId = b.fromUserId;
                    if (aId === activeChatId) return -1;
                    if (bId === activeChatId) return 1;
                    return 0;
                });

                for (const group of groupChats) {
                    const groupToUpdate = { ...group };
                    if (!groupToUpdate.groupId && groupToUpdate.fromUserId) {
                        groupToUpdate.groupId = groupToUpdate.fromUserId;
                    }

                    fetchAndUpdateGroupParticipants(groupToUpdate);
                }
            }

            // await storeChatsInDB(newChats);
            log(logId, '🕵️‍♂️ Updated DB and Redux with new chats');
        } else {
            log(logId, '🕵️‍♂️ No new chats found');
        }
    } catch (error) {
        if (!chatSyncLogs[logId]) chatSyncLogs[logId] = [];
        chatSyncLogs[logId].push({ type: 'error', message: 'Background sync error', error });
    }
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Main synchronization function - Cache-First Pattern
 * 1. Check IndexedDB for cached data
 * 2. If cache exists → use it (no API fetch)
 * 3. If no cache → fetch from API → store → update Redux
 */
export async function syncAllChats(options = {}) {
    const { progressRange = [35, 100] } = options;
    const [startProgress, endProgress] = progressRange;
    const chatFetchWeight = 0.4; // 40% of the remaining range for chat list

    const chatProgressEnd = startProgress + (endProgress - startProgress) * chatFetchWeight;

    chatSyncLogs = {};
    log('Step 0', '🚀 Starting chat synchronization (Cache-First)');
    const overallStartTime = performance.now();

    try {
        // Step 1: Check IndexedDB for cached data
        log('Step 1', '📦 Checking IndexedDB for cached chats...');
        const cachedChats = await recentChatIdb.getRecentChats();
        if (cachedChats.length > 0) {
            // Cache hit - use cached data, no API fetch needed
            log('Step 1', `✨ Found ${cachedChats.length} cached chats - using cached data`);

            // For cache hit, we jump progress to history sync part
            Store.dispatch(SyncProgressAction(Math.floor(chatProgressEnd)));

            await syncAllMessages(cachedChats, {
                ...options,
                progressRange: [chatProgressEnd, endProgress],
            });
            updateReduxStore(cachedChats);
            updateUnreadCountsFromChats(cachedChats); // Sync unread counts from cached data
            const cacheTime = logTime('Summary', '🎨 UI RELEASED (from cache)', overallStartTime);
            log('Summary', `✅ Cache hit! UI shown in ${cacheTime}s (no API fetch needed)`);

            // BACKGROUND SYNC: Fetch latest updates in forward direction
            log('Step 1', '🔄 Starting BACKGROUND SYNC for latest chats...', cachedChats[0]);
            // Background sync shouldn't update primary progress bar usually, but let's keep the await if it was there
            await fetchLatestChatsInBackground(CONFIG.lastSyncTime); // Pass latest messageTime

            // Efficiently update last sync times for messages
            const timeMapForMessages = {};
            const cacheLen = cachedChats.length;
            for (let i = 0; i < cacheLen; i++) {
                const chat = cachedChats[i];
                if (chat.fromUserId) {
                    timeMapForMessages[chat.fromUserId] = CONFIG.lastSyncTime;
                }
            }
            batchUpdateLastSyncMessagesTime(timeMapForMessages);
        } else {
            // Step 2: No cache - fetch from API
            log('Step 2', '📭 No cached chats found - fetching from server');

            // Initial fetch is backward to get history
            const freshChats = await fetchAllRecentChats('backward', percent => {
                const current = startProgress + (chatProgressEnd - startProgress) * percent;
                Store.dispatch(SyncProgressAction(Math.floor(current)));
            });

            if (freshChats.length === 0) {
                log('Step 2', '⚠️ No chats available');
                updateReduxStore([]);
                Store.dispatch(SyncProgressAction(endProgress));
                return;
            }

            Store.dispatch(SyncProgressAction(Math.floor(chatProgressEnd)));

            // Step 3: Store in IndexedDB
            await storeChatsInDB(freshChats);

            // Step 4: Update Redux
            updateReduxStore(freshChats);
            updateUnreadCountsFromChats(freshChats);

            await syncAllMessages(freshChats, {
                ...options,
                progressRange: [chatProgressEnd, endProgress],
            });
        }

        updateLastSyncTime(Date.now());
        Store.dispatch(SyncProgressAction(endProgress));

        // Step 5: Complete
        const totalDuration = logTime('Summary', '✅ SYNC COMPLETE', overallStartTime);
        logger.debug(`[ChatSync] ✅ SYNC COMPLETE: UI RELEASED in ${totalDuration}s`, {
            details: chatSyncLogs,
        });
    } catch (error) {
        if (!chatSyncLogs['Error']) chatSyncLogs['Error'] = [];
        chatSyncLogs['Error'].push({ type: 'error', message: 'Error during synchronization', error });
        logger.error('[ChatSync] ❌ Error during synchronization:', error, chatSyncLogs);
        throw error;
    }
}

/**
 * Reconnect synchronization - fetches only new/updated chats
 * For now, just re-runs full sync (can be optimized later)
 */
export async function syncReconnect() {
    log('Summary', '🔄 Reconnect sync - running full sync');
    return syncAllChats();
}
/**
 * Fetches and synchronizes recent chats based on parameters.
 * @param {Object} params - SDK fetch parameters
 */
export async function fetchAndSyncRecentChats(params = { limit: 10 }) {
    const response = await SDK.getRecentChatsByPagination(params);
    if (response?.statusCode === 200 && response.data) {
        await syncRecentChatBatch(response.data);
    }
    return response;
}

/**
 * Synchronizes a batch of recent chats:
 * 1. Stores in IndexedDB
 * 2. Updates Redux store
 * 3. Updates unread counts
 */
export async function syncRecentChatBatch(chats) {
    if (!Array.isArray(chats) || chats.length === 0) return;

    await storeChatsInDB(chats);
    updateReduxStore(chats);
    updateUnreadCountsFromChats(chats);
}
